import React, { useContext, useCallback } from 'react';
import { useIntl, FormattedMessage, defineMessages } from 'react-intl';
import { styled } from '@compiled/react';

import { token } from '@atlaskit/tokens';
import Button from '@atlaskit/button/default/button';
import Modal from '@atlaskit/modal-dialog/modal-dialog';
import ModalTransition from '@atlaskit/modal-dialog/modal-transition';
import ModalBody from '@atlaskit/modal-dialog/modal-body';
import ModalTitle from '@atlaskit/modal-dialog/modal-title';
import ModalHeader from '@atlaskit/modal-dialog/modal-header';
import ModalFooter from '@atlaskit/modal-dialog/modal-footer';
import { Checkbox } from '@atlaskit/checkbox/checkbox';

import { ImportStateContext } from '../ImportStateContextProvider.tsx';
import type { GoogleDrivePickerDoc } from '../../services/GoogleAPIs/GoogleDrivePicker.ts';
import { BULK_UPLOAD_LIMIT } from '../../config/limitForBulkUpload.ts';
import { useGetSelectedDocuments } from '../../hooks/useGetSelectedDocuments.ts';
import type { OneDriveFileType } from '../../services/OneDriveAPIs/OneDriveTypes.ts';
import { fixedBytesToMegabytes } from '../../utils/fixedBytesToMegabytes.ts';
import {
	getBulkTotalSize,
	isBulkLengthLimitReached,
	isBulkTotalSizeLimitReached,
} from '../../utils/checkBulkLimits.ts';
import { TOTAL_UPLOAD_LIMIT_BULK } from '../../config/fileUploadLimit.ts';
import type { Doc } from '../../hooks/useUploadReducer.ts';

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const SelectedDocumentsData = styled.div({
	display: 'flex',
	font: token('font.body.large'),
	marginBottom: token('space.100'),
	paddingLeft: token('space.050'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const SelectedDocumentsWrapper = styled.div({
	maxHeight: '180px',
	overflowY: 'auto',
	scrollbarWidth: 'thin',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'& > label': {
		cursor: 'pointer',
	},
});

export const i18n: {
	bulkLimitModalDialogCancelAction: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	bulkLimitModalDialogRetryAction: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	bulkLimitModalDialogRetryHeading: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	bulkLimitModalDialogRetryHeadingExtended: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	bulkLimitModalDialogDocsSelectedLength: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	bulkLimitModalDialogTotalSelectedDocsSize: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	bulkLimitModalDialogSizeUnit: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	bulkLimitModalDialogSizeCouldNotIdentify: {
		id: string;
		defaultMessage: string;
		description: string;
	};
} = defineMessages({
	bulkLimitModalDialogCancelAction: {
		id: 'import-from-sources.bulk-limit-modal.cancelAction',
		defaultMessage: 'Cancel',
		description: 'Cancels the files uploading and closes the upload modal',
	},
	bulkLimitModalDialogRetryAction: {
		id: 'import-from-sources.bulk-limit-modal.retryAction',
		defaultMessage: 'Retry',
		description: 'Restart import after filtering the documents',
	},
	bulkLimitModalDialogRetryHeading: {
		id: 'import-from-sources.bulk-limit-modal.bulkLimitModalDialogRetryHeading',
		defaultMessage: 'You can’t import more than {bulkLimit} documents at once.',
		description: 'The message indicating about the limit of the uploading files',
	},
	bulkLimitModalDialogRetryHeadingExtended: {
		id: 'import-from-sources.bulk-limit-modal.bulkLimitModalDialogRetryHeadingExtended',
		defaultMessage:
			'You can’t import more than {bulkLengthLimit, plural, one {# document} other {# documents}} at once, and the total size of all documents can’t be more than {bulkSizeLimit} MB.',
		description: 'The message indicating about the length and size limit of the uploading files',
	},
	bulkLimitModalDialogDocsSelectedLength: {
		id: 'import-from-sources.bulk-limit-modal.bulkLimitModalDialogDocsSelectedLength',
		defaultMessage: '{docsNumber, plural, one {# document} other {# documents}} selected',
		description: 'The message indicating how many documents are selected by a user',
	},
	bulkLimitModalDialogTotalSelectedDocsSize: {
		id: 'import-from-sources.bulk-limit-modal.bulkLimitModalDialogTotalSelectedDocsSize',
		defaultMessage: '{totalSize} MB total size',
		description: 'The message indicating selected documents total size',
	},
	bulkLimitModalDialogSizeUnit: {
		id: 'import-from-sources.bulk-limit-modal.bulkLimitModalDialogSizeUnit',
		defaultMessage: 'MB',
		description: 'The message indicating unit of documents size',
	},
	bulkLimitModalDialogSizeCouldNotIdentify: {
		id: 'import-from-sources.bulk-limit-modal.bulkLimitModalDialogSizeCouldNotIdentify',
		defaultMessage: 'can’t determine size',
		description: 'The message indicating unit of documents size',
	},
});

export const BulkLimitModal: React.FC<{
	onClose: () => void;
	onRetry: (docs: (GoogleDrivePickerDoc | File | OneDriveFileType)[]) => void;
}> = ({ onClose, onRetry }) => {
	const intl = useIntl();
	const {
		docActions: { uploadDocSelect },
		docs,
	} = useContext(ImportStateContext);

	const { documentsToUpload, selectedDocuments } = useGetSelectedDocuments();

	const handleRetry = () => onRetry(documentsToUpload);

	const handleChange = useCallback(
		({ target }: any) => {
			uploadDocSelect(target.value);
		},
		[uploadDocSelect],
	);

	const getCheckboxLabelText = (doc: Doc): string => {
		const { nameWithoutExt, size } = doc;
		const msg = size
			? `${fixedBytesToMegabytes(size)} ${intl.formatMessage(i18n.bulkLimitModalDialogSizeUnit)}`
			: intl.formatMessage(i18n.bulkLimitModalDialogSizeCouldNotIdentify);
		return `${nameWithoutExt} (${msg})`;
	};

	return (
		<ModalTransition>
			<Modal width="medium">
				<ModalHeader>
					<ModalTitle appearance="warning">
						<FormattedMessage
							{...i18n.bulkLimitModalDialogRetryHeadingExtended}
							values={{
								bulkLengthLimit: BULK_UPLOAD_LIMIT,
								bulkSizeLimit: fixedBytesToMegabytes(TOTAL_UPLOAD_LIMIT_BULK),
							}}
						/>
					</ModalTitle>
				</ModalHeader>

				<ModalBody>
					<SelectedDocumentsData>
						<FormattedMessage
							{...i18n.bulkLimitModalDialogDocsSelectedLength}
							values={{
								docsNumber: documentsToUpload.length,
							}}
						/>
					</SelectedDocumentsData>
					<SelectedDocumentsData>
						<FormattedMessage
							{...i18n.bulkLimitModalDialogTotalSelectedDocsSize}
							values={{
								totalSize: fixedBytesToMegabytes(getBulkTotalSize(selectedDocuments)),
							}}
						/>
					</SelectedDocumentsData>
					<SelectedDocumentsWrapper>
						{docs.map((doc, i) => {
							return (
								<Checkbox
									key={i}
									isChecked={doc.selected}
									label={getCheckboxLabelText(doc)}
									onChange={handleChange}
									value={doc.nameWithoutExt}
								/>
							);
						})}
					</SelectedDocumentsWrapper>
				</ModalBody>
				<ModalFooter>
					<>
						<Button
							key="cancel"
							appearance="subtle"
							onClick={onClose}
							testId="bulk-limit-modal-cancel"
						>
							<FormattedMessage {...i18n.bulkLimitModalDialogCancelAction} />
						</Button>
						<Button
							key="retry"
							appearance="primary"
							onClick={handleRetry}
							testId="bulk-limit-modal-retry"
							isDisabled={
								!documentsToUpload.length ||
								isBulkLengthLimitReached(documentsToUpload) ||
								isBulkTotalSizeLimitReached(selectedDocuments)
							}
						>
							<FormattedMessage {...i18n.bulkLimitModalDialogRetryAction} />
						</Button>
					</>
				</ModalFooter>
			</Modal>
		</ModalTransition>
	);
};
